import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Search, CheckCheck } from "lucide-react";
import { Header } from "../../components/Header/Header";
import useNotificationsStore from "../../store/useNotificationsStore";
import NotificationItem from "../../components/Notifications/NotificationItem";
import { notificationLink } from "../../components/Notifications/notificationHelpers";
import Pagination from "../../components/Pagination/Pagination";

const PAGE_SIZE = 20;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { isMenuOpen } = useOutletContext() || {};
  const [tab, setTab] = useState("all"); // "all" | "unread"
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);

  const items = useNotificationsStore((s) => s.items);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const loading = useNotificationsStore((s) => s.loading);
  const hasMore = useNotificationsStore((s) => s.hasMore);
  const loadingMore = useNotificationsStore((s) => s.loadingMore);
  const loadMore = useNotificationsStore((s) => s.loadMore);
  const loadAll = useNotificationsStore((s) => s.loadAll);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const remove = useNotificationsStore((s) => s.remove);

  // Fetch everything once; search + pagination happen on the client below.
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Client-side tab + search filtering.
  const filtered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    let result = tab === "unread" ? items.filter((n) => !n.is_read) : items;
    if (q) {
      result = result.filter(
        (n) =>
          (n.body || "").toLowerCase().includes(q) ||
          (n.title || "").toLowerCase().includes(q),
      );
    }
    return result;
  }, [items, tab, searchInput]);

  // Client-side pagination (numbered pages, matching the other list pages).
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const visible = useMemo(() => {
    const end = currentPage * rowsPerPage;
    return filtered.slice(end - rowsPerPage, end);
  }, [filtered, currentPage, rowsPerPage]);

  // Reset to the first page whenever the tab / search changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [tab, searchInput]);

  // When the user reaches the last loaded page and older notifications exist on
  // the server, transparently pull the next page so numbered pagination keeps
  // flowing without a separate "load more" control.
  useEffect(() => {
    if (currentPage >= totalPages && hasMore && !loadingMore) {
      loadMore();
    }
  }, [currentPage, totalPages, hasMore, loadingMore, loadMore]);

  const activate = (n) => {
    if (!n.is_read) markRead(n.id);
    const link = notificationLink(n);
    if (link) navigate(link);
  };

  const isEmpty = !loading && filtered.length === 0;

  const TabButton = ({ id, label, badge }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`relative pb-1.5 text-[14px] font-medium transition-colors ${
        tab === id ? "text-[#A04A97]" : "text-[#71717A] hover:text-[#52525B]"
      }`}
    >
      <span className="flex items-center gap-2">
        {label}
        <span
          className={`text-[11px] px-1.5 py-0.5 rounded-full ${
            tab === id
              ? "bg-[#F3E3F1] text-[#A04A97]"
              : "bg-[#F4F4F5] text-[#71717A]"
          }`}
        >
          {badge}
        </span>
      </span>
      {tab === id && (
        <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#A04A97] rounded-full" />
      )}
    </button>
  );

  const markAllButton = (
    <button
      type="button"
      onClick={() => markAllRead()}
      disabled={unreadCount === 0}
      className={`flex items-center gap-2 px-3 h-9 rounded-md border text-[14px] font-medium transition-colors ${
        unreadCount === 0
          ? "border-[#E4E4E7] text-[#C4C4C4] cursor-default"
          : "border-[#DEDEDE] text-[#A04A97] hover:bg-[#FAFAFA]"
      }`}
    >
      <CheckCheck size={16} /> Mark all as Read
    </button>
  );

  return (
    <div className="flex flex-col h-full w-full font-outfit overflow-hidden bg-[#FAFAFA]">
      <div className="flex-shrink-0 bg-white">
        <Header
          name="Notifications"
          icon={""}
          isMenuOpen={isMenuOpen}
          isKnowledgeBasePage={false}
          headerRightContent={markAllButton}
        />
      </div>

      <div className="flex items-center justify-between gap-4 px-6 pt-2 bg-white border-b border-[#0000000D] flex-shrink-0 w-full">
        <div className="flex items-center gap-6 mt-4">
          <TabButton id="all" label="All Notifications" badge={items.length} />
          <TabButton id="unread" label="Unread" badge={unreadCount} />
        </div>
        <div className="flex items-center gap-3 pb-3">
          <div className="relative w-[380px] max-w-[38vw]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search notifications..."
              className="w-full pl-9 pr-3 h-9 text-[14px] rounded-lg border border-[#0000001A] bg-white focus:outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE]"
            />
          </div>
        </div>
      </div>

      {/* List (scrollable area full width; card contained + centered) */}
      <div className="flex-1 overflow-y-auto px-6 py-2">
        {/* List */}
        <div className="bg-white rounded-xl ring-1 ring-black ring-opacity-5 overflow-hidden max-w-[960px] mx-auto">
          {loading && items.length === 0 ? (
            <div className="px-4 py-16 text-center text-[13px] text-[#A1A1AA]">
              Loading…
            </div>
          ) : isEmpty ? (
            <div className="px-4 py-20 flex flex-col items-center text-center">
              <div className="w-28 h-28 rounded-full bg-[#F0EFEE] flex items-center justify-center mb-6">
                <img
                  src="/bell.svg"
                  alt="No notifications"
                  className="w-16 h-16"
                />
              </div>
              <p className="font-outfit text-[24px] font-semibold leading-normal text-center text-[#3F3F46]">
                No notifications yet
              </p>
              <p className="font-outfit text-[16px] font-normal leading-[150%] text-center text-[#71717A] mt-2 max-w-[360px]">
                You're all caught up! New notifications will appear here when
                there's activity.
              </p>
            </div>
          ) : (
            <>
              {visible.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onActivate={activate}
                  onMarkRead={markRead}
                  onDelete={remove}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {/* Fixed pagination footer (matches the other list pages). */}
      {!isEmpty && (
        <div className="w-full bg-white border-t border-black/10 flex-shrink-0 z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </div>
      )}
    </div>
  );
}
